import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Stethoscope, Sparkles, X, Search, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useLang } from "@/context/LanguageContext";

export default function Diagnose() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const { ids: favIds } = useFavorites();
  const [allTests, setAllTests] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/tests").then((r) => setAllTests(r.data)).catch(() => toast.error(t("dx.errLoad")));
  }, [lang, t]);

  // Preselect favorites if coming from /favoriter or ?source=fav
  useEffect(() => {
    if (params.get("source") === "fav" || (favIds.length > 0 && selected.size === 0 && allTests.length > 0)) {
      // Only auto-select when source query indicates it
      if (params.get("source") === "fav") {
        setSelected(new Set(favIds));
      }
    }
    // eslint-disable-next-line
  }, [params, allTests]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const filtered = useMemo(() => {
    if (!filter) return allTests;
    const q = filter.toLowerCase();
    return allTests.filter(
      (tst) => tst.name.toLowerCase().includes(q) || tst.purpose.toLowerCase().includes(q) || tst.region.toLowerCase().includes(q)
    );
  }, [allTests, filter]);

  const selectedTests = useMemo(
    () => allTests.filter((tst) => selected.has(tst.id)),
    [allTests, selected]
  );

  const run = async () => {
    if (selected.size === 0) return toast.warning(t("dx.selectFirst"));
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/diagnose", {
        positive_test_ids: Array.from(selected),
        notes: notes.trim() || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(t("dx.errRun"));
    } finally {
      setLoading(false);
    }
  };

  const importFavorites = () => {
    if (favIds.length === 0) return toast.warning(t("dx.noFav"));
    setSelected(new Set([...selected, ...favIds]));
    toast.success(t("dx.added", { n: favIds.length }));
  };

  return (
    <div data-testid="diagnose-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
        <span className="title-rule" />{t("dx.eyebrow")}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight mb-3">
        {t("dx.title1")} <em className="text-accent italic font-serif">{t("dx.titleAccent")}</em>
      </h1>
      <p className="text-sm text-muted-foreground max-w-2xl mb-8">
        {t("dx.lead")}
      </p>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT – tests selector */}
        <div className="lg:col-span-3">
          <Card className="p-4 bg-card">
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t("dx.search")}
                  className="pl-9"
                  data-testid="dx-search"
                />
              </div>
              {favIds.length > 0 && (
                <Button variant="outline" size="sm" onClick={importFavorites} data-testid="dx-import-fav">
                  {t("dx.importFav", { n: favIds.length })}
                </Button>
              )}
            </div>

            <div className="max-h-[480px] overflow-y-auto pr-1 space-y-1.5" data-testid="dx-tests-list">
              {filtered.map((tst) => {
                const checked = selected.has(tst.id);
                return (
                  <label
                    key={tst.id}
                    className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition-colors border ${
                      checked ? "bg-accent/10 border-accent/40" : "border-transparent hover:bg-muted/50"
                    }`}
                    data-testid={`dx-row-${tst.id}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(tst.id)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">{tst.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {tst.region} · {tst.purpose}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>
        </div>

        {/* RIGHT – sticky panel */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <Card className="p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg font-medium">{t("dx.selected")}</h3>
                <Badge variant="secondary" className="font-mono" data-testid="dx-selected-count">{selected.size}</Badge>
              </div>
              {selected.size === 0 ? (
                <p className="text-xs text-muted-foreground py-2">
                  {t("dx.empty")}
                </p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {selectedTests.map((tst) => (
                    <div key={tst.id} className="flex items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1.5">
                      <span className="flex-1 truncate">{tst.name}</span>
                      <button onClick={() => toggle(tst.id)} className="text-muted-foreground hover:text-destructive">
                        <X className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5 block">
                  {t("dx.notes")}
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("dx.notesPh")}
                  className="resize-none min-h-[90px] text-sm"
                  data-testid="dx-notes"
                />
              </div>

              <Button
                onClick={run}
                disabled={loading || selected.size === 0}
                className="w-full mt-4"
                data-testid="dx-run-btn"
              >
                {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Stethoscope className="size-4 mr-2" />}
                {t("dx.run")}
              </Button>
            </Card>

            {result && (
              <Card className="p-5 bg-card rise" data-testid="dx-result">
                <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-accent mb-3">
                  <Sparkles className="size-3.5" /> {t("dx.result")}
                </div>
                <div className="markdown-body text-sm">
                  <ReactMarkdown>{result.summary}</ReactMarkdown>
                </div>
                <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-border">
                  {t("dx.disclaimer")}
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {!result && (
        <Card className="mt-10 p-5 bg-secondary/30 border-dashed">
          <div className="flex items-start gap-3">
            <div className="size-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Sparkles className="size-4" />
            </div>
            <div className="text-sm">
              <p className="font-medium mb-1">{t("dx.howTitle")}</p>
              <p className="text-muted-foreground leading-relaxed">
                {t("dx.how")}
              </p>
              <Link to="/assistent" className="inline-flex items-center gap-1 mt-2 text-primary text-xs hover:text-accent">
                {t("dx.discuss")} <ArrowRight className="size-3" />
              </Link>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
